import { Button, Col, Icon, Input, Row, Sidepanel, Switch, Text } from 'components';
import {
  Api,
  DURATION_FIELDS,
  QUIZ_TYPE,
  canManageHalfTime,
  msToParts,
  partsToMs,
  theme
} from 'lib';
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { ContentHeader } from 'screens/Admin/components';
import GamePicker from './GamePicker';

const DateInput = styled.input`
  width: 100%;
  height: 44px;
  border-radius: 8px;
  border: 1px solid ${(props) => (props.hasError ? theme.SECONDARY : theme.GREY_30)};
  padding: 0 14px;
  font-family: CeraPro;
  font-size: 14px;
  color: ${theme.GREY_80};
  background: white;
  &:focus {
    outline: none;
    border-color: ${theme.PRIMARY};
  }
`;

const Tab = styled.button`
  flex: 1;
  height: 40px;
  border: 1px solid ${(props) => (props.active ? theme.PRIMARY : theme.GREY_30)};
  background: ${(props) => (props.active ? `${theme.PRIMARY}15` : 'white')};
  color: ${(props) => (props.active ? theme.PRIMARY : theme.GREY_60)};
  font-family: CeraPro;
  font-size: 14px;
  font-weight: ${(props) => (props.active ? 600 : 400)};
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  opacity: ${(props) => (props.disabled ? 0.55 : 1)};
  &:first-child {
    border-radius: 8px 0 0 8px;
  }
  &:last-child {
    border-radius: 0 8px 8px 0;
    border-left: none;
  }
`;

// Compact segmented control for the content language (English / 日本語).
const LangPill = styled.button`
  height: 30px;
  padding: 0 14px;
  border: 1px solid ${(props) => (props.active ? theme.SECONDARY : theme.GREY_30)};
  background: ${(props) => (props.active ? `${theme.SECONDARY}15` : 'white')};
  color: ${(props) => (props.active ? theme.SECONDARY : theme.GREY_60)};
  font-family: CeraPro;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  &:first-child {
    border-radius: 8px 0 0 8px;
  }
  &:last-child {
    border-radius: 0 8px 8px 0;
    border-left: none;
  }
`;

// Neutral informational note (e.g. "quiz type can't be changed").
const HintBanner = styled.div`
  background: ${theme.GREY_20};
  border: 1px solid ${theme.GREY_30};
  color: ${theme.GREY_80};
  border-radius: 8px;
  padding: 10px 14px;
  font-family: CeraPro;
  font-size: 13px;
  margin-bottom: 16px;
`;

// Warnings & errors render as plain red text (no bordered/background box).
const WarningText = styled.div`
  color: ${theme.SECONDARY};
  font-family: CeraPro;
  font-size: 13px;
  margin-bottom: 16px;
`;

/**
 * A plain input, not the shared `Input`/`DefaultInput` component the rest of this form uses.
 *
 * `Input`'s outer Wrapper carries a `preventDefault()` + manual `inputRef.focus()` click handler
 * (marked "TODO: SHOULD BE FIXED URGENTLY" at components/Input/index.js) that, with three of
 * these sitting immediately adjacent, was sending focus to the box AFTER the one actually
 * clicked — pick "days" and "hours" would focus instead. Sidestepping it here rather than
 * touching that shared handler, which every other input in the app also goes through.
 */
const DurationDigitInput = styled.input`
  width: 72px;
  height: 43px;
  box-sizing: border-box;
  padding: 0 8px;
  border-radius: 50px;
  border: 1px solid ${theme.GREY_50};
  font-family: CeraPro;
  font-size: 16px;
  text-align: center;

  -moz-appearance: textfield;
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

// Convert a Date / ISO string to the YYYY-MM-DDTHH:mm string a datetime-local input expects,
// expressed in the user's local timezone.
const toDatetimeLocal = (input) => {
  if (!input) return '';
  const d = new Date(input);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(
    d.getMinutes()
  )}`;
};

// Treat the datetime-local string as local-time and return a UTC ISO string for the API.
const fromDatetimeLocal = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
};

const localTzLabel = (() => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (e) {
    return 'local time';
  }
})();

const QuizForm = ({
  visible = true,
  user,
  teamId,
  mode = 'create',
  quiz,
  onClose,
  onSaved,
  onConflict,
  initialDraft,
  onDraftChange
}) => {
  const isEdit = mode === 'edit';
  const initialType =
    quiz?.quizType ||
    (canManageHalfTime(user) ? QUIZ_TYPE.DAILY : QUIZ_TYPE.DAILY);

  const [quizType, setQuizType] = useState(initialType);
  const [gameId, setGameId] = useState(
    quiz?.game?._id || (typeof quiz?.game === 'string' ? quiz.game : '') || ''
  );
  const [question, setQuestion] = useState(quiz?.question || '');
  // Multi-language content (UI only for now): Japanese question/answers are collected and
  // kept in the shareable draft, but the submit payload is unchanged until the backend
  // supports translations. English stays the source of truth for structure.
  const [contentLang, setContentLang] = useState('en');
  const [questionJa, setQuestionJa] = useState(quiz?.questionJa || '');
  const [rewardPoints, setRewardPoints] = useState(
    quiz?.rewardPoints != null ? String(quiz.rewardPoints) : ''
  );
  const [isActive, setIsActive] = useState(quiz ? quiz.isActive : true);
  const [startDate, setStartDate] = useState(toDatetimeLocal(quiz?.startDate));
  const [endDate, setEndDate] = useState(toDatetimeLocal(quiz?.endDate));
  /**
   * Game Day only: how long the quiz accepts answers, from the half-time whistle.
   *
   * Held as { days, hours, minutes } rather than milliseconds, so typing "25" into hours does not
   * normalise to "1 day 1 hour" under the cursor. Converted once on submit.
   *
   * Blank throughout means "send nothing" — the platform default, where answering closes at the end
   * of the 15-minute break and when the second half begins. That is why this is not seeded with a
   * default: a pre-filled value would opt every new quiz into overriding the phase gate.
   */
  const [answerWindow, setAnswerWindow] = useState(msToParts(quiz?.answerWindowMs));
  const [answers, setAnswers] = useState(
    quiz?.answers?.length
      ? quiz.answers.map((a) => ({
          _id: a._id,
          text: a.text,
          textJa: a.textJa || '',
          isCorrect: !!a.isCorrect
        }))
      : [
          { text: '', textJa: '', isCorrect: true },
          { text: '', textJa: '', isCorrect: false }
        ]
  );

  const [saving, setSaving] = useState(false);
  const [topError, setTopError] = useState(null);
  const [scheduleError, setScheduleError] = useState(null);
  const [answerWindowError, setAnswerWindowError] = useState(null);
  const [conflictQuizId, setConflictQuizId] = useState(null);

  // The panel stays mounted so its slide-in/out animation can run. Re-seed every
  // field whenever the panel reopens (visible flips false→true) so the form
  // reflects the current `quiz` / `mode` props instead of stale state.
  useEffect(() => {
    if (!visible) return;
    // A shared draft (from the URL) takes precedence over the quiz/defaults so an
    // in-progress create/edit reopens exactly as it was left.
    const d = initialDraft || null;
    const fallbackType =
      quiz?.quizType || (canManageHalfTime(user) ? QUIZ_TYPE.DAILY : QUIZ_TYPE.DAILY);
    setQuizType(d?.quizType || fallbackType);
    setGameId(
      d?.gameId ?? (quiz?.game?._id || (typeof quiz?.game === 'string' ? quiz.game : '') || '')
    );
    setQuestion(d?.question ?? quiz?.question ?? '');
    setContentLang('en');
    setQuestionJa(d?.questionJa ?? quiz?.questionJa ?? '');
    setRewardPoints(
      d?.rewardPoints != null
        ? String(d.rewardPoints)
        : quiz?.rewardPoints != null
          ? String(quiz.rewardPoints)
          : ''
    );
    setIsActive(d ? !!d.isActive : quiz ? quiz.isActive : true);
    setStartDate(d?.startDate ?? toDatetimeLocal(quiz?.startDate));
    setEndDate(d?.endDate ?? toDatetimeLocal(quiz?.endDate));
    setAnswerWindow(d?.answerWindow ?? msToParts(quiz?.answerWindowMs));
    setAnswers(
      d?.answers?.length
        ? d.answers.map((a) => ({
            _id: a._id,
            text: a.text,
            textJa: a.textJa || '',
            isCorrect: !!a.isCorrect
          }))
        : quiz?.answers?.length
          ? quiz.answers.map((a) => ({
              _id: a._id,
              text: a.text,
              textJa: a.textJa || '',
              isCorrect: !!a.isCorrect
            }))
          : [
              { text: '', textJa: '', isCorrect: true },
              { text: '', textJa: '', isCorrect: false }
            ]
    );
    setSaving(false);
    setTopError(null);
    setScheduleError(null);
    setConflictQuizId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  // Mirror the live form state into the URL (debounced) so the open draft is shareable.
  useEffect(() => {
    if (!visible || !onDraftChange) return;
    const draft = {
      quizType,
      gameId,
      question,
      questionJa,
      rewardPoints,
      isActive,
      startDate,
      endDate,
      answerWindow,
      answers
    };
    const t = setTimeout(() => onDraftChange(draft), 300);
    return () => clearTimeout(t);
  }, [
    visible,
    onDraftChange,
    quizType,
    gameId,
    question,
    questionJa,
    rewardPoints,
    isActive,
    startDate,
    endDate,
    answerWindow,
    answers
  ]);

  const isDaily = quizType === QUIZ_TYPE.DAILY;
  const isHalfTime = quizType === QUIZ_TYPE.HALF_TIME;

  const canShowHalfTimeTab = canManageHalfTime(user);

  const handleAnswerText = (i, value) => {
    setAnswers((prev) => {
      const next = [...prev];
      next[i] = { ...next[i], text: value };
      return next;
    });
  };

  const handleAnswerTextJa = (i, value) => {
    setAnswers((prev) => {
      const next = [...prev];
      next[i] = { ...next[i], textJa: value };
      return next;
    });
  };

  const handleAnswerCorrect = (i) => {
    setAnswers((prev) => prev.map((a, idx) => ({ ...a, isCorrect: idx === i })));
  };

  const addAnswer = () => {
    if (answers.length >= 10) return;
    setAnswers((prev) => [...prev, { text: '', textJa: '', isCorrect: false }]);
  };

  const removeAnswer = (i) => {
    if (answers.length <= 2) return;
    setAnswers((prev) => {
      const next = prev.filter((_, idx) => idx !== i);
      if (!next.some((a) => a.isCorrect)) next[0].isCorrect = true;
      return next;
    });
  };

  const validate = () => {
    if (!question.trim()) return 'Question is required';
    if (!rewardPoints || Number(rewardPoints) < 0) return 'Reward points must be 0 or greater';
    const trimmed = answers.filter((a) => a.text.trim().length > 0);
    if (trimmed.length < 2) return 'Provide at least 2 answers';
    if (!trimmed.some((a) => a.isCorrect)) return 'Select a correct answer';
    const norm = (s) => s.trim().toLowerCase();
    if (new Set(trimmed.map((a) => norm(a.text))).size !== trimmed.length)
      return 'Duplicate options are not allowed. Please enter a unique option.';
    const jaTexts = trimmed.map((a) => a.textJa).filter((t) => t && t.trim());
    if (new Set(jaTexts.map(norm)).size !== jaTexts.length)
      return 'Duplicate Japanese options are not allowed. Please enter a unique option.';
    if (isDaily && !isEdit) {
      if (!startDate || !endDate) return 'Daily quizzes require start and end times';
      if (new Date(endDate) <= new Date(startDate))
        return 'End time must be after start time';
    }
    if (isDaily && isEdit && (startDate || endDate)) {
      if (!startDate || !endDate) return 'Both start and end times are required';
      if (new Date(endDate) <= new Date(startDate))
        return 'End time must be after start time';
    }
    if (isHalfTime && !gameId) return 'Game Day quizzes need a game';
    return null;
  };

  const parseScheduleConflict = (err) => {
    const m = /Schedule overlaps with existing quiz (\w+)/.exec(err || '');
    return m ? m[1] : null;
  };

  const handleSubmit = async () => {
    const localErr = validate();
    if (localErr) {
      setTopError(localErr);
      return;
    }

    // A native number input still accepts "+", "-", ".", and "e" — none of that is a valid
    // days/hours/minutes value, so only a plain, whole, non-negative digit string passes here.
    // Blank is fine; it just means the admin hasn't set that field.
    if (
      isHalfTime &&
      Object.values(answerWindow || {}).some((v) => v !== '' && v != null && !/^\d+$/.test(v))
    ) {
      setAnswerWindowError('Invalid Input');
      return;
    }

    setTopError(null);
    setScheduleError(null);
    setAnswerWindowError(null);
    setConflictQuizId(null);
    setSaving(true);

    const validAnswers = answers
      .filter((a) => a.text.trim().length > 0)
      .map((a) => {
        const ans = { text: a.text.trim(), isCorrect: !!a.isCorrect };
        if (a.textJa && a.textJa.trim()) ans.textJa = a.textJa.trim();
        // Send the existing option _id on edit so the backend keeps its prior votes
        // linked instead of re-minting the id and wiping the result distribution.
        if (a._id) ans._id = a._id;
        return ans;
      });

    let res;
    if (isEdit) {
      const payload = {
        id: quiz._id,
        question: question.trim(),
        questionJa: questionJa.trim(),
        answers: validAnswers,
        rewardPoints: Number(rewardPoints),
        isActive
      };
      if (isDaily) {
        if (startDate) payload.startDate = fromDatetimeLocal(startDate);
        if (endDate) payload.endDate = fromDatetimeLocal(endDate);
      }
      if (isHalfTime) {
        // `null` clears it on the server, restoring the default close — which is what an admin
        // emptying every field means. Sending 0 instead would be rejected by the one-minute floor.
        const ms = partsToMs(answerWindow);
        payload.answerWindowMs = ms > 0 ? ms : null;
      }
      res = await Api.put('/admin/quizzes/update', payload, null, { silent: true });
    } else {
      const payload = {
        question: question.trim(),
        questionJa: questionJa.trim() || undefined,
        teamId,
        quizType,
        answers: validAnswers,
        rewardPoints: Number(rewardPoints),
        isActive
      };
      if (isHalfTime) {
        payload.gameId = gameId;
        // Omitted entirely when blank, rather than sent as 0 — the create endpoint has no "clear"
        // semantics, and absence is what selects the platform default.
        const ms = partsToMs(answerWindow);
        if (ms > 0) payload.answerWindowMs = ms;
      } else {
        if (gameId) payload.gameId = gameId;
        payload.startDate = fromDatetimeLocal(startDate);
        payload.endDate = fromDatetimeLocal(endDate);
      }
      res = await Api.post('/admin/quizzes/create', payload, null, { silent: true });
    }

    setSaving(false);

    if (res && res.err && res.status) {
      const err = res.err;
      if (res.status === 409) {
        setScheduleError(err);
        const conflictId = parseScheduleConflict(err);
        if (conflictId) {
          setConflictQuizId(conflictId);
          onConflict && onConflict(conflictId);
        }
        return;
      }
      if (res.status === 400) {
        if (/start|end|schedule|date/i.test(err)) {
          setScheduleError(err);
        } else {
          setTopError(err);
        }
        return;
      }
      setTopError(err);
      return;
    }

    if (res?.data || res?.msg) {
      onSaved(res.data || null);
      onClose();
    }
  };

  // Japanese translation progress — question plus each answer that has English text.
  const isJa = contentLang === 'ja';
  const jaSlots = [
    questionJa,
    ...answers.filter((a) => a.text && a.text.trim()).map((a) => a.textJa)
  ];
  const jaDone = jaSlots.filter((v) => v && v.trim()).length;
  const jaTotal = jaSlots.length;

  // Indices of answers whose text (in the currently-viewed language) duplicates an earlier
  // one — trimmed, case-insensitive. Drives the live red-border + inline warning.
  const duplicateAnswerIdx = useMemo(() => {
    const seen = new Map();
    const dupes = new Set();
    answers.forEach((a, i) => {
      const key = ((isJa ? a.textJa : a.text) || '').trim().toLowerCase();
      if (!key) return;
      if (seen.has(key)) {
        dupes.add(i);
        dupes.add(seen.get(key));
      } else {
        seen.set(key, i);
      }
    });
    return dupes;
  }, [answers, isJa]);
  const hasDuplicateAnswers = duplicateAnswerIdx.size > 0;

  const headerText = useMemo(() => {
    if (isEdit) return `Edit ${quiz?.quizType === QUIZ_TYPE.HALF_TIME ? 'Game Day' : 'daily'} quiz`;
    return isHalfTime ? 'Create Game Day quiz' : 'Create daily quiz';
  }, [isEdit, isHalfTime, quiz]);

  return (
    <Sidepanel visible={visible} wid={720} pad="0" closeModal={() => {}}>
      <ContentHeader text={headerText} closePanel={onClose} />
      <Col wid="100%" pad="20px 24px">
        {!isEdit && canShowHalfTimeTab && (
          <Row noFlex marg="0 0 16px">
            <Tab
              type="button"
              active={isDaily}
              onClick={() => setQuizType(QUIZ_TYPE.DAILY)}
            >
              Daily
            </Tab>
            <Tab
              type="button"
              active={isHalfTime}
              onClick={() => setQuizType(QUIZ_TYPE.HALF_TIME)}
            >
              Game Day
            </Tab>
          </Row>
        )}

        {isEdit && (
          <HintBanner>
            Quiz type and game can&apos;t be changed — delete and recreate to switch.
          </HintBanner>
        )}

        {topError && <WarningText>{topError}</WarningText>}

        {/* Content language — Japanese is collected in the UI only for now; the save
            payload is unchanged until the backend supports translations. */}
        <Row between center noFlex marg="0 0 12px" gap="12px">
          <Col noFlex style={{ minWidth: 0 }}>
            <Text h4 marg="0">
              Content language
            </Text>
            <Text c2 col={theme.GREY_60} marg="2px 0 0">
              Japanese is optional — empty fields fall back to English in the fan app.
            </Text>
          </Col>
          <Row noFlex center>
            <LangPill type="button" active={!isJa} onClick={() => setContentLang('en')}>
              English
            </LangPill>
            <LangPill type="button" active={isJa} onClick={() => setContentLang('ja')}>
              日本語 · {jaDone}/{jaTotal}
            </LangPill>
          </Row>
        </Row>

        {isJa && (
          <HintBanner>
            Translating to Japanese. Answers, the correct answer, schedule and settings are
            managed on the English tab — here you only translate the text. English text is
            shown as the placeholder.
          </HintBanner>
        )}

        <Col wid="100%" marg="0 0 16px">
          <Input
            title={isJa ? 'Question (日本語)' : 'Question *'}
            placeholder={isJa ? question || '質問を入力してください' : 'Enter the question'}
            value={isJa ? questionJa : question}
            onChange={(e) => (isJa ? setQuestionJa(e.target.value) : setQuestion(e.target.value))}
            wid="100%"
          />
        </Col>

        <Col wid="100%" marg="0 0 16px">
          <Input
            title="Reward points *"
            type="number"
            placeholder="0"
            value={rewardPoints}
            onChange={(e) => setRewardPoints(e.target.value)}
            wid="100%"
          />
        </Col>

        {/* DAILY quizzes have an optional game; the picker is role-aware (GamePicker uses the
            owner-scoped /admin/quizzes/upcomingGames for team admins), so it loads for everyone.
            HALF_TIME requires a game and is P4P-only — `isHalfTime` can only be true for P4P
            admins since team admins never see the Game Day tab. */}
        {(isHalfTime || (isDaily && !isEdit)) && (
          <Col wid="100%" marg="0 0 16px">
            <Text h4 marg="0 0 8px">
              Game{isHalfTime ? ' *' : ' (optional)'}
            </Text>
            {/* refreshKey={visible}: this panel stays mounted for the whole page session, so
                without it the game list was fetched once and never again — and a fixture deleted
                and recreated between two opens kept offering the old, dead `_id`. */}
            <GamePicker
              teamId={teamId}
              value={gameId}
              onChange={setGameId}
              required={isHalfTime}
              disabled={isEdit}
              refreshKey={visible}
            />
          </Col>
        )}

        {isHalfTime && (
          <Col wid="100%" marg="0 0 16px">
            <Text h4 marg="0 0 4px">
              Answerable for (optional)
            </Text>
            <Text c2 col={theme.GREY_60} marg="0 0 8px">
              Counted from the half-time whistle. Leave blank to close at the end of the 15-minute
              break, which is also when the second half begins. Set it and the quiz stays playable
              for this long even after play resumes — never past the time the card stops showing.
            </Text>
            <Row gap="8px">
              {DURATION_FIELDS.map((field) => (
                <Col key={field.key} noFlex centerAll>
                  <DurationDigitInput
                    // type="number" was the actual hole: for text a browser can't parse as a
                    // number (multiple signs, trailing junk, etc.), the input's `.value` silently
                    // reports "" while the field still visibly shows what was typed — so
                    // `e.target.value` in onChange never saw "++3" at all, it saw "". Plain text
                    // means `.value` always matches what's on screen, and the regex check on save
                    // actually sees what the admin typed.
                    type="text"
                    inputMode="numeric"
                    value={String(answerWindow?.[field.key] ?? '')}
                    onChange={(e) => {
                      // A value long enough to overflow this narrow box read as garbled/
                      // overlapping digits rather than just scrolling — cap it before that happens.
                      if (e.target.value.length > 4) return;
                      setAnswerWindowError(null);
                      setAnswerWindow((prev) => ({ ...prev, [field.key]: e.target.value }));
                    }}
                  />
                  <Text c2 col={theme.GREY_60} marg="4px 0 0">
                    {field.label}
                  </Text>
                </Col>
              ))}
            </Row>
            {answerWindowError && (
              <WarningText style={{ margin: '4px 0 0' }}>{answerWindowError}</WarningText>
            )}
          </Col>
        )}

        {isDaily && (
          <Row wid="100%" marg="0 0 16px" gap="12px">
            <Col flex={1}>
              <Text h4 marg="0 0 8px">
                Start *
              </Text>
              <DateInput
                type="datetime-local"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                hasError={!!scheduleError}
              />
            </Col>
            <Col flex={1}>
              <Text h4 marg="0 0 8px">
                End *
              </Text>
              <DateInput
                type="datetime-local"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                hasError={!!scheduleError}
              />
            </Col>
          </Row>
        )}

        {isDaily && (
          <Text c2 col={theme.GREY_60} marg="-8px 0 16px">
            Times shown in your local time ({localTzLabel}); stored in UTC.
          </Text>
        )}

        {scheduleError && (
          <WarningText>
            {scheduleError}
            {conflictQuizId && (
              <>
                {' '}
                <Text inline c2 col={theme.SECONDARY}>
                  Conflicting quiz id: {conflictQuizId}
                </Text>
              </>
            )}
          </WarningText>
        )}

        <Col wid="100%" marg="0 0 16px">
          <Row between center marg="0 0 8px">
            <Text h4>
              {isJa ? 'Answers — 日本語' : 'Answers (2–10, select the correct one)'}
            </Text>
            {!isJa && (
              <Button
                text="Add answer"
                onClick={addAnswer}
                bordered
                disabled={answers.length >= 10}
                style={{ width: 'fit-content', padding: '4px 12px', fontSize: 13 }}
              />
            )}
          </Row>

          {answers.map((ans, i) => (
            <Row key={i} center marg="0 0 8px">
              {/* In the Japanese view the correct marker is read-only context. */}
              <Row
                centerAll
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  border: `1px solid ${ans.isCorrect ? theme.GREEN : theme.GREY_40}`,
                  backgroundColor: ans.isCorrect ? theme.GREEN : 'transparent',
                  cursor: isJa ? 'default' : 'pointer',
                  marginRight: 10
                }}
                onClick={() => !isJa && handleAnswerCorrect(i)}
              >
                {ans.isCorrect && <Icon type="check" col="white" size={12} />}
              </Row>
              <Col flex={1} marg="0 10px 0 0">
                <Input
                  placeholder={isJa ? ans.text || `回答 ${i + 1}` : `Answer ${i + 1}`}
                  value={isJa ? ans.textJa || '' : ans.text}
                  onChange={(e) =>
                    isJa
                      ? handleAnswerTextJa(i, e.target.value)
                      : handleAnswerText(i, e.target.value)
                  }
                  error={duplicateAnswerIdx.has(i)}
                  wid="100%"
                  noTitle
                />
              </Col>
              {!isJa && (
                <Icon
                  type="close"
                  col={answers.length > 2 ? theme.GREY_60 : theme.GREY_20}
                  onClick={() => removeAnswer(i)}
                  style={{ cursor: answers.length > 2 ? 'pointer' : 'not-allowed' }}
                />
              )}
            </Row>
          ))}
          {hasDuplicateAnswers && (
            <WarningText style={{ margin: '4px 0 0' }}>
              {isJa
                ? 'Duplicate Japanese options are not allowed. Please enter a unique option.'
                : 'Duplicate options are not allowed. Please enter a unique option.'}
            </WarningText>
          )}
        </Col>

        <Row between center marg="0 0 24px">
          <Text>Active</Text>
          <Switch activated={isActive} onClick={() => setIsActive((v) => !v)} />
        </Row>

        <Row
          center
          justifyContent="flex-end"
          pad="16px 0 8px"
          style={{ borderTop: `1px solid ${theme.GREY_20}` }}
        >
          <Button
            text="Cancel"
            onClick={onClose}
            bordered
            marg="0 12px 0 0"
            style={{ width: 'fit-content', opacity: 0.8 }}
          />
          <Button
            text={saving ? 'Saving...' : isEdit ? 'Save changes' : 'Create quiz'}
            onClick={handleSubmit}
            disabled={saving}
            col={theme.PRIMARY}
            style={{ width: 'fit-content' }}
          />
        </Row>
      </Col>
    </Sidepanel>
  );
};

export default QuizForm;
